import { MINIMUM_BAR_WIDTH_CSS_PX, toBarPixelInterval } from './bar-geometry.js';
/** Fewest simultaneously rendered stacking rows a System_Administrator may configure (R10.15). */
export const MIN_STACKING_ROW_CAP = 5;
/** Most simultaneously rendered stacking rows a System_Administrator may configure (R10.15). */
export const MAX_STACKING_ROW_CAP = 50;
/** The configured default, per R10.15 and assumption A9. */
export const DEFAULT_STACKING_ROW_CAP = 10;
/**
 * Whether `value` is a stacking row cap R10.15 permits: a whole number from
 * {@link MIN_STACKING_ROW_CAP} through {@link MAX_STACKING_ROW_CAP}.
 *
 * Exposed because the Master_Data_Administration screen and the configuration
 * endpoint validate the same number this module refuses to run with, and one
 * definition of the permitted range is better than three.
 */
export function isStackingRowCap(value) {
    return (Number.isInteger(value) && value >= MIN_STACKING_ROW_CAP && value <= MAX_STACKING_ROW_CAP);
}
/** Throws unless `value` is a cap R10.15 permits. */
export function assertStackingRowCap(value) {
    if (!isStackingRowCap(value)) {
        throw new RangeError(`stacking row cap must be an integer from ${String(MIN_STACKING_ROW_CAP)} to ${String(MAX_STACKING_ROW_CAP)}, received ${String(value)}`);
    }
}
/**
 * Whether two rendered bars overlap visually.
 *
 * Intervals are half-open, `[leftPx, rightPx)`: bars that merely touch - one
 * ending exactly where the next begins - do not overlap and share a lane. That is
 * the same strictness the assignment loop applies, stated once so the property
 * test and the algorithm cannot drift apart.
 */
export function barPixelIntervalsOverlap(a, b) {
    return a.leftPx < b.rightPx && b.leftPx < a.rightPx;
}
/**
 * Assigns each bar of one stacking grouping to a lane so that no two bars in a
 * lane overlap in rendered pixel space (R10.11), capping the rendered rows and
 * reporting the remainder (R10.15).
 *
 * Greedy interval partitioning: sort by left pixel edge then identifier, then put
 * each bar in the lowest lane whose last bar ends at or before this bar's left
 * edge. `O(n log n)` for the sort plus `O(n · lanes)` for the scan; at
 * Planning_Volume - about 1,500 bars against a 10-row cap - that is well under a
 * millisecond, which is why the layout can be recomputed on every viewport change
 * instead of cached.
 *
 * Throws on a cap outside the R10.15 range, on a non-positive column width, and
 * on a duplicate identifier.
 */
export function assignCalendarLanes(bars, options) {
    const { columnWidthPx } = options;
    const rowCap = options.rowCap ?? DEFAULT_STACKING_ROW_CAP;
    const minimumWidthPx = options.minimumWidthPx ?? MINIMUM_BAR_WIDTH_CSS_PX;
    assertStackingRowCap(rowCap);
    const ordered = [];
    const seen = new Set();
    for (const bar of bars) {
        if (seen.has(bar.id)) {
            throw new RangeError(`duplicate bar identifier in one stacking grouping: ${bar.id}`);
        }
        seen.add(bar.id);
        // `toBarPixelInterval` validates the column width and the minimum, and applies
        // R10.4's widening. Lanes are assigned on what it returns, never on the dates.
        ordered.push({
            id: bar.id,
            pixels: toBarPixelInterval(bar.geometry, columnWidthPx, minimumWidthPx),
        });
    }
    ordered.sort(byLeftEdgeThenId);
    /** Right edge of the last bar placed in each lane, by lane index. */
    const laneEndPx = [];
    const placed = [];
    const overflow = [];
    const lanes = new Map();
    for (const bar of ordered) {
        // `<=` and not `<`: touching bars share a lane, overlapping ones never do.
        let lane = laneEndPx.findIndex((endPx) => endPx <= bar.pixels.leftPx);
        if (lane === -1) {
            lane = laneEndPx.length;
            laneEndPx.push(Number.NEGATIVE_INFINITY);
        }
        laneEndPx[lane] = bar.pixels.rightPx;
        const assigned = { id: bar.id, lane, pixels: bar.pixels };
        if (lane >= rowCap) {
            overflow.push(assigned);
            continue;
        }
        placed.push(assigned);
        lanes.set(bar.id, lane);
    }
    return {
        rowCap,
        columnWidthPx,
        placed,
        overflow,
        lanes,
        laneCount: Math.min(laneEndPx.length, rowCap),
        unrenderedCount: overflow.length,
        requiredRowCap: laneEndPx.length,
    };
}
/**
 * The lane a bar occupies, or `undefined` when the cap kept it off screen.
 *
 * Spelt out rather than left to `assignment.lanes.get`, because "absent" and
 * "lane zero" are easy to confuse at a call site and the renderer has to
 * distinguish them.
 */
export function laneOfBar(assignment, id) {
    return assignment.lanes.get(id);
}
/**
 * The deterministic order of R18.14: ascending left pixel edge, then ascending
 * identifier by code unit.
 *
 * Total, because identifiers are unique within a grouping - so no two bars ever
 * compare equal and the outcome cannot depend on the input order or on the
 * engine's sort stability.
 */
function byLeftEdgeThenId(a, b) {
    if (a.pixels.leftPx !== b.pixels.leftPx) {
        return a.pixels.leftPx - b.pixels.leftPx;
    }
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
//# sourceMappingURL=lane-assignment.js.map